import React, { useEffect, useState } from 'react';
import { getStayAccommodation } from 'Components/utils/booking/bookingUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { PLE_BOOKING_STAYS_PAGE_ID } from 'Routes/constants';
import { STATIC_PAGE_TYPES } from 'Tracking/constants';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import {
  CUSTOMER_ACCOUNT_GET_CANCELED_TRIPS,
  CUSTOMER_ACCOUNT_GET_CURRENT_TRIPS,
  CUSTOMER_ACCOUNT_GET_CUSTOMERS,
  CUSTOMER_ACCOUNT_GET_FUTURE_TRIPS,
  CUSTOMER_ACCOUNT_GET_PAST_TRIPS,
} from 'Utils/request/requestDictionary';
import { useGQLErrorsHandler } from 'Utils/use-gql-errors-handler/useGQLErrorsHandler';
import {
  getCutomersQuery,
  DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT,
  DEFAULT_PAGINATED_TRIPS_LIST_LIMIT,
  getCustomerBookingsQuery,
} from 'Views/account/queries/account.queries';
import { PROSPECT_CUSTOMER } from 'Views/booking/constants';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { StaysView } from './StaysView';

type CustomersGqlData = { customerType?: string };
type TripsListGql = { data?: BookingEntity[]; pageInfo?: { hasNextPage?: boolean } };
type BookingsGqlData = {
  futureTrips?: TripsListGql;
  currentTrips?: TripsListGql;
  pastTrips?: TripsListGql;
  canceledTrips?: TripsListGql;
};

const mapStaysAccommodation = (bookings: Array<BookingEntity>): Array<BookingEntity> =>
  bookings.map((booking) => {
    return {
      ...booking,
      stays: (booking.stays || []).map((stay) => {
        if (!(stay.accommodations as unknown[] | undefined)?.length) {
          return stay;
        }

        return {
          ...stay,
          accommodation: getStayAccommodation(stay.accommodations, stay.product, []),
        };
      }),
    };
  });

export const StaysViewContainer = () => {
  const appContext = useAppContext();
  const [futureAndCurrentTrips, setFutureAndCurrentTrips] = useState<{
    data: BookingEntity[];
    pagination: { hasNextPage?: boolean };
  }>({
    data: [],
    pagination: {},
  });
  const [pastTrips, setPastTrips] = useState<{
    data: BookingEntity[];
    pagination: { hasNextPage?: boolean };
  }>({
    data: [],
    pagination: {},
  });
  const {
    data: customersData,
    error: customersError,
    isLoading: isCustomersLoading,
  } = useGQL({
    query: getCutomersQuery(),
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_CUSTOMERS,
      cacheFront: {
        key: CUSTOMER_ACCOUNT_GET_CUSTOMERS,
      },
    },
    cache: false,
    map: ({ customers }) => ({
      customerType: customers.type,
    }),
  });
  const customersTyped = customersData as CustomersGqlData | undefined;
  const hasBookingsQueriesDisabled =
    !customersTyped || customersTyped.customerType === PROSPECT_CUSTOMER;
  const {
    data: bookingsData,
    error: bookingsError,
    isLoading: isBookingsLoading,
  } = useGQL(
    {
      query: getCustomerBookingsQuery(
        appContext,
        {
          period: 'future',
          limit: DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT,
        },
        {
          withUpsells: true,
        },
      ),
      queryOptions: {
        requestName: CUSTOMER_ACCOUNT_GET_FUTURE_TRIPS,
      },
      cache: false,
      ignore: hasBookingsQueriesDisabled,
      map: ({ customerBookings }) => ({
        futureTrips: customerBookings,
      }),
    },
    {
      query: getCustomerBookingsQuery(
        appContext,
        {
          period: 'current',
          limit: DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT,
        },
        {
          withUpsells: false,
        },
      ),
      queryOptions: {
        requestName: CUSTOMER_ACCOUNT_GET_CURRENT_TRIPS,
      },
      cache: false,
      ignore: hasBookingsQueriesDisabled,
      map: ({ customerBookings }) => ({
        currentTrips: customerBookings,
      }),
    },
    {
      query: getCustomerBookingsQuery(
        appContext,
        {
          period: 'past',
          limit: DEFAULT_PAGINATED_TRIPS_LIST_LIMIT,
        },
        {
          withUpsells: false,
        },
      ),
      queryOptions: {
        requestName: CUSTOMER_ACCOUNT_GET_PAST_TRIPS,
      },
      cache: false,
      ignore: hasBookingsQueriesDisabled,
      map: ({ customerBookings }) => ({
        pastTrips: customerBookings,
      }),
    },
    {
      query: getCustomerBookingsQuery(
        appContext,
        {
          period: 'all',
          limit: DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT,
          isCanceled: true,
        },
        {
          withUpsells: false,
        },
      ),
      queryOptions: {
        requestName: CUSTOMER_ACCOUNT_GET_CANCELED_TRIPS,
      },
      cache: false,
      ignore: hasBookingsQueriesDisabled,
      map: ({ customerBookings }) => ({
        canceledTrips: customerBookings,
      }),
    },
  );
  const bookingsTyped = bookingsData as BookingsGqlData | undefined;
  useGQLErrorsHandler(customersError, bookingsError, {
    catchAuthError: true,
    catchNotFoundError: true,
  });
  useEffect(() => {
    if (bookingsTyped?.futureTrips || bookingsTyped?.currentTrips) {
      setFutureAndCurrentTrips({
        data: [
          ...(bookingsTyped?.currentTrips?.data || []),
          ...mapStaysAccommodation(bookingsTyped?.futureTrips?.data || []),
        ].filter((booking) => booking.categories.isStandard),
        pagination: bookingsTyped?.futureTrips?.pageInfo,
      });
    }

    if (bookingsTyped?.pastTrips) {
      setPastTrips({
        data: bookingsTyped?.pastTrips?.data || [],
        pagination: bookingsTyped?.pastTrips?.pageInfo,
      });
    }
  }, [bookingsTyped]);
  const isDataLoading =
    isCustomersLoading ||
    isBookingsLoading ||
    (!hasBookingsQueriesDisabled && !bookingsData && !bookingsError);

  if (isDataLoading) {
    return (
      <div className="fixed inset-0 z-100 flex flex-col items-center justify-center bg-white">
        <Spinner iconName={ICONS.TRIDENT1} />
      </div>
    );
  }

  return (
    <StaysView
      hasNoBookings={!Object.values(bookingsTyped || {}).some((key) => Boolean(key?.data?.length))}
      bookings={{
        future: futureAndCurrentTrips.data,
        past: pastTrips.data,
        canceled: bookingsTyped?.canceledTrips?.data || [],
        hasMoreFutureTrips: !!futureAndCurrentTrips?.pagination?.hasNextPage,
        hasMorePastTrips: !!pastTrips?.pagination?.hasNextPage,
        hasMoreCanceledTrips: false,
      }}
    />
  );
};
StaysViewContainer.pageId = PLE_BOOKING_STAYS_PAGE_ID;
StaysViewContainer.pageType = STATIC_PAGE_TYPES.PLE;
StaysViewContainer.requireAuthentication = true;
